import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import RecentAlerts, { SendsTable, latestAcrossFleet } from '../components/AdminRecentAlerts';
import { plusChip, plusAdminLine } from '../utils/plusStatus';


function timeSince(ms) {
  if (!ms) return 'never';
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function fmtDateTime(ms) {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleString(); } catch { return '—'; }
}

function StatusDot({ online }) {
  return <span className={`status-dot ${online ? 'status-dot--online' : 'status-dot--offline'}`} />;
}

// Renders the uptime figure from the adminDeviceUptime result. `state` is null
// while loading, 'error' on failure, or the result object.
//
// A null figure is never shown as 0% or as a bare dash: the server says WHY it
// has no number, and the difference matters when you are deciding whether a
// device is broken. "Too new" and "silent for a week" look nothing alike.
const UPTIME_REASONS = {
  'too-new':     'too new to measure',
  'no-readings': 'no readings yet',
  'no-data':     'no history yet',
  'no-device':   '—',
};
function fmtUptime(state, key) {
  if (state === null) return '…';
  if (state === 'error') return '—';
  const v = state[key];
  if (v == null) return UPTIME_REASONS[state.reason] || 'no data yet';
  // Fast mode reports every 10s against a slower steady cadence, so the figure
  // clamps to 100% and isn't a like-for-like measurement. Say so rather than
  // showing a confident number.
  return state.fastMode ? `${v}% (fast mode)` : `${v}%`;
}

// Every alert a customer can have, in the order the Alerts page shows them, so
// what support sees on the phone matches what the customer is looking at.
//
// Replaces a raw dump of the alerts object. That dump was honest but unreadable
// — nested keys, missing fields simply absent — and the question support
// actually has is "is email on, is their phone verified, which levels fire",
// which it never answered at a glance.
const ALERT_CHANNELS = [
  { key: 'enabled',     label: 'Email' },
  { key: 'pushEnabled', label: 'Push' },
  { key: 'smsEnabled',  label: 'Text' },
];
const ALERT_LEVELS = [
  { key: 'low30', label: 'Below 30%' },
  { key: 'low20', label: 'Below 20%' },
  { key: 'low10', label: 'Below 10%' },
  { key: 'full',  label: 'Almost full' },
];
const ALERT_FAMILIES = [
  { path: 'usageOn', label: 'Usage alerts' },
  { path: 'leak.on', label: 'Slow leak watch' },
  { path: 'away.on', label: 'Away mode' },
  { path: 'offline', label: 'Device offline' },
  { path: 'monthlyRecap', label: 'Monthly report' },
];

// Mirrors TIME_OPTIONS / IRRIGATION_OPTIONS / the defaults in
// components/LeakUsageSection.js. All four must agree: the admin and the
// customer are setting the same field, and adminUpdateAlerts validates against
// the same bounds server-side.
const QUIET_TIME_OPTIONS = [];
for (let m = 0; m < 24 * 60; m += 30) {
  const hr = Math.floor(m / 60), min = m % 60;
  const h12 = hr % 12 === 0 ? 12 : hr % 12;
  QUIET_TIME_OPTIONS.push({ v: m / 60, label: `${h12}:${min === 0 ? '00' : '30'} ${hr < 12 ? 'AM' : 'PM'}` });
}
const DEFAULT_QUIET = { startHour: 1, endHour: 5 };
const IRRIGATION_OPTIONS = [30, 60, 90, 120, 150, 180, 210, 240].map((m) => {
  const h = Math.floor(m / 60), min = m % 60;
  return { v: m, label: h && min ? `${h} hr ${min} min` : h ? `${h} hr` : `${min} min` };
});
const DEFAULT_IRRIGATION_MIN = 60;

// Mirrors MAX_RULES / GALLON_PRESETS / WINDOW_OPTIONS / DEFAULT_RULE in
// components/LeakUsageSection.js, and the bounds adminUpdateAlerts enforces.
const MAX_USAGE_RULES = 5;
const FIRST_RULE_ID = 'rule1';
const DEFAULT_USAGE_RULE = { on: true, gallons: 100, windowMinutes: 60 };
const GALLON_PRESETS = [50, 100, 150, 200, 300, 500];
const USAGE_WINDOW_OPTIONS = [
  { v: 30, label: '30 min' }, { v: 60, label: '1 hr' }, { v: 120, label: '2 hr' },
  { v: 360, label: '6 hr' }, { v: 720, label: '12 hr' }, { v: 1440, label: '1 day' },
];
function newUsageRuleId() {
  return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// What a rule's gallons actually mean on THIS tank.
//
// Gallons are not comparable across tanks and the difference is not small: on a
// 5000 gal tank with 4 ft of usable depth, 100 gallons is about one inch; on a
// tall narrow tank the same 100 gallons is most of a foot. Printing the inch
// equivalent is the quickest way to see which kind of tank you are looking at.
function inchesForGallons(gallons, tank) {
  const cap = tank && typeof tank.capacity === 'number' ? tank.capacity : 0;
  const fullFt = tank && typeof tank.fullFt === 'number' ? tank.fullFt : 0;
  const emptyFt = tank && typeof tank.emptyFt === 'number' ? tank.emptyFt : 0;
  if (!(cap > 0) || !(fullFt > emptyFt)) return null;
  const galPerInch = cap / ((fullFt - emptyFt) * 12);
  if (!(galPerInch > 0)) return null;
  return gallons / galPerInch;
}

// One indented line under a family toggle, for the tuning value that family
// carries. Rendered only while the family is on — off, the setting has no
// effect and the row would be noise.
function AdminSubRow({ children }) {
  return <div className="admin-alert-row admin-alert-row--sub">{children}</div>;
}

function AdminMiniSelect({ value, options, disabled, label, onChange }) {
  return (
    <span className="admin-select-wrap">
      <select className="admin-sort admin-mini-select" aria-label={label}
        value={String(value)} disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      <IconChevronDown />
    </span>
  );
}

function readPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

// `pill` overrides the on/off badge text. Used when "off" would be misleading
// — a blocked SMS recipient reads as off, but nobody here can switch it back.
function AdminToggle({ on, label, disabled, hint, pill, onToggle }) {
  return (
    <div className="admin-alert-row">
      <button type="button" className={`admin-toggle ${on ? 'admin-toggle--on' : ''}`}
        disabled={disabled} aria-pressed={on} aria-label={`${label}: ${on ? 'on' : 'off'}`}
        onClick={() => onToggle(!on)}>
        <span className="admin-toggle__knob" />
      </button>
      <span className="admin-alert-row__label">{label}</span>
      <span className={`admin-pill ${pill ? 'admin-pill--blocked'
        : on ? 'admin-pill--on' : 'admin-pill--off'}`}>{pill || (on ? 'on' : 'off')}</span>
      {hint && <span className="admin-alert-row__hint">{hint}</span>}
    </div>
  );
}

// The rapid-loss rules, as an editable list. Replaced wholesale on every
// change — the same way the customer's own editor writes them — so there is one
// storage shape rather than a merge nobody tested.
// One rule. customOpen is LOCAL state, not derived from the value, so choosing
// "Custom…" opens the box without writing anything — deriving it from the
// gallons would mean nudging the stored number just to change the input type.
// The gallons the detector will actually use for this rule.
//
// leak.js validateUsageRules clamps every rule to max(1, capacity * 0.005) —
// so a rule set below that silently behaves as if it were set AT it. Showing
// the clamp is a fact about the product, not a guess about the sensor: on a
// 5000 gal tank the floor is 25 gal, about a quarter inch.
//
// An earlier version of this flagged anything under two inches as "near sensor
// noise". That number was invented. The drift actually measured on a static
// tank was ~0.33 in over an hour and ~0.54 in over twelve, so a one-inch rule
// has roughly 3x margin — the warning was wrong and would have talked support
// out of perfectly good settings.
function usageFloorGal(tank) {
  const cap = tank && typeof tank.capacity === 'number' ? tank.capacity : 0;
  return cap > 0 ? Math.max(1, cap * 0.005) : null;
}

function UsageRuleRow({ rule, tanks, busy, onPatch, onRemove }) {
  const [customOpen, setCustomOpen] = useState(!GALLON_PRESETS.includes(rule.gallons));
  // A rule is not tank-scoped — the detector runs it against EVERY tank on the
  // device — and the same gallons are a different depth in each. So show one
  // figure per tank rather than picking one tank's geometry and implying it
  // speaks for both.
  const perTank = (tanks || []).map((tk) => ({
    num: tk.num,
    name: tk.name || `Tank ${tk.num}`,
    inches: inchesForGallons(rule.gallons, tk),
    floorGal: usageFloorGal(tk),
  })).filter((x) => x.inches != null);
  // Below the clamp the stored number is not the one that fires. Flag if that
  // is true on ANY tank, since the rule is wrong there whatever the other says.
  const clampedOn = perTank.filter((x) => x.floorGal != null && rule.gallons < x.floorGal);

  return (
    <AdminSubRow>
      <button type="button"
        className={`admin-toggle admin-toggle--sm ${rule.on === true ? 'admin-toggle--on' : ''}`}
        disabled={busy} aria-pressed={rule.on === true}
        aria-label={`Rule ${rule.id}: ${rule.on === true ? 'on' : 'off'}`}
        onClick={() => onPatch({ on: !(rule.on === true) })}>
        <span className="admin-toggle__knob" />
      </button>

      {customOpen ? (
        <input type="number" className="admin-mini-input" min="1" max="99999" step="1"
          aria-label="Gallons" disabled={busy} defaultValue={rule.gallons}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            // Only ever send a value the server will take. A half-typed or
            // empty box must not blank the rule.
            if (Number.isInteger(n) && n >= 1 && n <= 99999) onPatch({ gallons: n });
          }} />
      ) : (
        <AdminMiniSelect label="Gallons" disabled={busy} value={rule.gallons}
          options={[...GALLON_PRESETS.map((g) => ({ v: g, label: `${g} gal` })), { v: -1, label: 'Custom…' }]}
          onChange={(v) => { if (v === -1) setCustomOpen(true); else onPatch({ gallons: v }); }} />
      )}
      <span className="admin-alert-row__sublabel">gal in</span>
      <AdminMiniSelect label="Window" disabled={busy} value={rule.windowMinutes}
        options={USAGE_WINDOW_OPTIONS}
        onChange={(v) => onPatch({ windowMinutes: v })} />

      {perTank.length > 0 && (
        <span className={`admin-alert-row__hint ${clampedOn.length ? '' : 'admin-alert-row__hint--calm'}`}>
          ≈ {perTank.map((x) => {
            const n = x.inches < 10 ? x.inches.toFixed(1) : Math.round(x.inches);
            return perTank.length > 1 ? `${n} in (${x.name})` : `${n} in`;
          }).join(' · ')}
          {clampedOn.map((x) => ` — raised to ${Math.round(x.floorGal)} gal${perTank.length > 1 ? ` on ${x.name}` : ''}`).join('')}
        </span>
      )}

      {/* The first rule is permanent in the customer's editor; keep that so
          admin and customer are looking at the same list. */}
      {rule.id !== FIRST_RULE_ID && (
        <button type="button" className="admin-rule-remove" disabled={busy}
          aria-label={`Remove rule ${rule.id}`} onClick={onRemove}>
          Remove
        </button>
      )}
    </AdminSubRow>
  );
}

// The rapid-loss rules as an editable list. Replaced wholesale on every change
// — the same way the customer's own editor writes them — so there is one
// storage shape rather than a merge nobody tested.
function UsageRuleRows({ rules, tanks, busy, onRules }) {
  return (
    <>
      {rules.map((r) => (
        <UsageRuleRow key={r.id} rule={r} tanks={tanks} busy={busy}
          onPatch={(patch) => onRules(rules.map((x) => (x.id === r.id ? { ...x, ...patch } : x)))}
          onRemove={() => onRules(rules.filter((x) => x.id !== r.id))} />
      ))}
      <AdminSubRow>
        <button type="button" className="admin-rule-add"
          disabled={busy || rules.length >= MAX_USAGE_RULES}
          onClick={() => onRules([...rules, { id: newUsageRuleId(), ...DEFAULT_USAGE_RULE }])}>
          + Add rule
        </button>
        {rules.length >= MAX_USAGE_RULES && (
          <span className="admin-alert-row__sublabel">{MAX_USAGE_RULES} is the limit</span>
        )}
      </AdminSubRow>
    </>
  );
}

function AlertsPanel({ claim, deviceId, tanks, busy, onChange }) {
  const a = claim.alerts || {};
  const t = a.thresholds || {};
  const custom = t.custom || {};
  const sms = a.smsEnabled === true;
  // Defaults match what the detectors assume when the customer never set them,
  // so the admin sees the window that is actually in force, not a blank.
  const quiet = (a.leak && a.leak.quiet) || DEFAULT_QUIET;
  const irrig = (a.away && a.away.irrigation) || {};
  // The customer's editor shows a default first rule when none is stored, so
  // show the same thing — otherwise support sees nothing and assumes nothing
  // fires, when in fact the stored-empty case still uses this default.
  const usageRules = Array.isArray(a.usage) && a.usage.length
    ? a.usage
    : [{ id: FIRST_RULE_ID, ...DEFAULT_USAGE_RULE }];
  // Texts need BOTH the per-device opt-in and a verified number on the account.
  // Saying which one is missing saves the "it's on but they get nothing" call.
  //
  // An opt-out outranks both. When the customer has replied STOP, Twilio blocks
  // the number for our sender and the switch below reads off — which looks like
  // a setting someone can change, when in fact nothing we do here restores it.
  // Only they can, by texting START.
  const smsHint = claim.smsOptOutAt
    ? `blocked — they replied STOP on ${fmtDateTime(claim.smsOptOutAt)}. `
      + 'Only they can undo it, by texting START to the alert number.'
    : sms
      ? (claim.phoneVerified ? null : 'no verified phone — nothing will send')
      : null;

  return (
    <div className="admin-account__alerts">
      <div className="admin-account__alerts-label">Alerts</div>

      <div className="admin-alert-group">How they're notified</div>
      {ALERT_CHANNELS.map(({ key, label }) => (
        <AdminToggle key={key} label={label} on={a[key] === true} disabled={busy}
          hint={key === 'smsEnabled'
            ? (smsHint || (sms ? null : 'can only be switched off here'))
            : undefined}
          pill={key === 'smsEnabled' && claim.smsOptOutAt ? 'blocked' : undefined}
          // Texts are off-only from admin: switching them ON has to be the
          // customer's own act so their opt-in is on record. The server
          // refuses it too; this just avoids offering a button that fails.
          onToggle={(next) => {
            if (key === 'smsEnabled' && next === true) return;
            onChange({ [key]: next });
          }} />
      ))}

      <div className="admin-alert-group">Water level</div>
      {ALERT_LEVELS.map(({ key, label }) => (
        <AdminToggle key={key} label={label} on={t[key] === true} disabled={busy}
          onToggle={(next) => onChange({ thresholds: { [key]: next } })} />
      ))}
      <AdminToggle label={`Custom${custom.value ? ` (${custom.value}%)` : ''}`}
        on={custom.on === true} disabled={busy}
        hint={custom.on === true && !custom.value ? 'no level set' : undefined}
        onToggle={(next) => onChange({ thresholds: { custom: { on: next } } })} />

      <div className="admin-alert-group">Usage and leak alerts</div>
      {ALERT_FAMILIES.map(({ path, label }) => {
        const on = readPath(a, path) === true;
        return (
          <React.Fragment key={path}>
            <AdminToggle label={label} on={on} disabled={busy}
              onToggle={(next) => {
                if (path.includes('.')) {
                  const [head] = path.split('.');
                  onChange({ [head]: { on: next } });
                } else {
                  onChange({ [path]: next });
                }
              }} />

            {path === 'usageOn' && on && (
              <UsageRuleRows rules={usageRules} tanks={tanks} busy={busy}
                onRules={(next) => onChange({ usage: next })} />
            )}

            {/* The overnight watch only looks inside these hours, so when
                someone says "it never catches anything" this is the first
                thing to check. */}
            {path === 'leak.on' && on && (
              <AdminSubRow>
                <span className="admin-alert-row__sublabel">Quiet hours</span>
                <AdminMiniSelect label="Quiet hours start" disabled={busy}
                  value={quiet.startHour} options={QUIET_TIME_OPTIONS}
                  onChange={(v) => onChange({ leak: { quiet: { startHour: v } } })} />
                <span className="admin-alert-row__sublabel">to</span>
                <AdminMiniSelect label="Quiet hours end" disabled={busy}
                  value={quiet.endHour} options={QUIET_TIME_OPTIONS}
                  onChange={(v) => onChange({ leak: { quiet: { endHour: v } } })} />
              </AdminSubRow>
            )}

            {/* Watering that runs longer than this reads as a leak, which is
                the usual cause of "away mode keeps alerting me". */}
            {path === 'away.on' && on && (
              <AdminSubRow>
                <button type="button"
                  className={`admin-toggle admin-toggle--sm ${irrig.on === true ? 'admin-toggle--on' : ''}`}
                  disabled={busy} aria-pressed={irrig.on === true}
                  aria-label={`Ignore irrigation: ${irrig.on === true ? 'on' : 'off'}`}
                  onClick={() => onChange({ away: { irrigation: { on: !(irrig.on === true) } } })}>
                  <span className="admin-toggle__knob" />
                </button>
                <span className="admin-alert-row__sublabel">Ignore irrigation up to</span>
                <AdminMiniSelect label="Irrigation run time" disabled={busy || irrig.on !== true}
                  value={irrig.estMinutes || DEFAULT_IRRIGATION_MIN} options={IRRIGATION_OPTIONS}
                  onChange={(v) => onChange({ away: { irrigation: { estMinutes: v } } })} />
              </AdminSubRow>
            )}
          </React.Fragment>
        );
      })}

      <RecentAlerts sends={claim.recentSends} multiTank={Array.isArray(tanks) && tanks.filter(Boolean).length > 1} />

      <div className="admin-alert-foot">
        Changes save immediately to {claim.email || 'this account'} on {deviceId}.
      </div>
    </div>
  );
}

// Poll-cadence presets offered in the admin editor. The server (adminUpdateDevice)
// validates the actual range (10–3600s); these are just the sensible choices.
const CADENCE_PRESETS = [10, 30, 60, 300, 900];

// Mirrors LED_MODES in Settings.js and ADMIN_LED_MODES in the cloud. All three
// must agree — the callable rejects anything outside its own allowlist.
const LED_MODE_OPTIONS = [
  { v: 'always_on',    l: 'Always on' },
  { v: 'button_30s',   l: '30 s after button' },
  { v: 'smart_notify', l: 'Smart notify' },
];
function cadenceLabel(v) {
  if (v === 30) return '30 s (default)';
  if (v === 10) return '10 s (fast)';
  if (v >= 60) return `${v / 60} min`;
  return `${v} s`;
}

function IconChevronDown() {
  return (
    <svg className="admin-select-wrap__chev" width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  );
}

function IconPencil() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

// One editable field inside an .admin-facts grid.
//
// Reads exactly like the read-only facts beside it until the pencil is clicked,
// so the panel stays a dense reference sheet rather than turning into a form.
// Saving is explicit: Enter or the tick commits, Escape or the × abandons. No
// save-on-blur -- these values change what a customer's alerts fire on, and a
// stray click should never be enough to alter them.
function EditableFact({ label, value, suffix, type = 'text', hint, saving, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const begin = () => { setDraft(value == null ? '' : String(value)); setEditing(true); };
  const commit = () => {
    const raw = draft.trim();
    const next = raw === '' ? null : (type === 'number' ? Number(raw) : raw);
    setEditing(false);
    // Unchanged → don't spend a write, an audit entry, or a device push.
    if (next === value || (next == null && value == null)) return;
    if (type === 'number' && next != null && !Number.isFinite(next)) return;
    onSave(next);
  };

  if (editing) {
    return (
      <div className="admin-facts__edit">
        <span>{label}</span>
        <div className="admin-facts__editrow">
          <input
            autoFocus
            className="admin-search admin-facts__input"
            type={type === 'number' ? 'number' : 'text'}
            step="any"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            aria-label={label}
          />
          <button type="button" className="admin-facts__btn" onClick={commit} aria-label="Save">✓</button>
          <button type="button" className="admin-facts__btn" onClick={() => setEditing(false)} aria-label="Cancel">×</button>
        </div>
        {hint && <span className="admin-edit__hint">{hint}</span>}
      </div>
    );
  }

  return (
    <div>
      <span>{label}</span>
      <b>
        {value != null && value !== '' ? `${value}${suffix || ''}` : '—'}
        <button
          type="button"
          className="admin-facts__pencil"
          onClick={begin}
          disabled={saving}
          aria-label={`Edit ${label}`}
          title={`Edit ${label}`}
        ><IconPencil /></button>
      </b>
    </div>
  );
}

// Sensor height for the console: absolute depth over the sensor, in inches to
// the nearest quarter. Quarter-inch because that is the resolution the customer
// sees in ft-in mode, and finer digits here would imply precision the sensor
// does not have.
function fmtSensorIn(ft) {
  if (typeof ft !== 'number' || !Number.isFinite(ft)) return '—';
  return `${(Math.round(ft * 12 * 4) / 4).toFixed(2).replace(/\.?0+$/, '')} in`;
}

function fmtGal(g) {
  if (typeof g !== 'number' || !Number.isFinite(g)) return '—';
  return `${g.toLocaleString()} gal`;
}

// Display-unit choices. Mirrors DEPTH_FORMATS in Settings.js and
// ADMIN_DEPTH_FORMATS in the cloud; the callable rejects anything else.
const DEPTH_FORMAT_OPTIONS = [
  { v: 'ft-in',      l: 'Feet & inches' },
  { v: 'ft-decimal', l: 'Decimal feet' },
  { v: 'inches',     l: 'Inches only' },
];

// ── Detail modal for one device ──────────────────────────────────
function DeviceDetail({ dev, onClose, onSaved }) {
  const [savingField, setSavingField] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareMsg, setShareMsg] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [resending, setResending] = useState(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseId, setReleaseId] = useState('');
  const [releaseEmail, setReleaseEmail] = useState('');
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [releaseMsg, setReleaseMsg] = useState(null);
  const [saveError, setSaveError]     = useState(null);
  const [uptime, setUptime]           = useState(null);   // { uptimePct, uptime24hPct, spanHours } | 'error'
  const [warns, setWarns]             = useState([]);    // soft server-side warnings

  // Fetch connectivity uptime once, when this device's detail opens. One doc
  // read per open (the hourly_7d aggregate), server-side and admin-gated.
  useEffect(() => {
    if (!dev) return undefined;
    let cancelled = false;
    setUptime(null);
    (async () => {
      try {
        const call = httpsCallable(functions, 'adminDeviceUptime');
        // The cadence is NOT sent any more. This used to pass
        // `dev.steadyPollSec || 30`, which scored a 300s device against a 30s
        // denominator at 10% whenever that value hadn't loaded — and correctly
        // when it had, so the number changed on refresh. The server reads it
        // from the device doc now.
        const res = await call({ deviceId: dev.deviceId });
        if (!cancelled) setUptime(res.data || {});
      } catch (err) {
        console.error('adminDeviceUptime failed:', err);
        if (!cancelled) setUptime('error');
      }
    })();
    return () => { cancelled = true; };
  }, [dev]);

  // All admin edits route through the admin-claim-gated callable — never a
  // direct Firestore write (the rules keep these fields client-unwritable).
  const save = async (patch, fieldKey) => {
    setSavingField(fieldKey);
    setSaveError(null);
    try {
      const call = httpsCallable(functions, 'adminUpdateDevice');
      await call({ deviceId: dev.deviceId, ...patch });
      onSaved(dev.deviceId, patch);
    } catch (err) {
      console.error('adminUpdateDevice failed:', err);
      setSaveError(err?.message || 'Save failed.');
    } finally {
      setSavingField(null);
    }
  };

  // "Time until empty" trial on/off for this monitor. The server works out the
  // rate as soon as it's switched on, so refetch.
  const saveSupplyTrial = async (on) => {
    setSavingField('supplyTrial');
    setSaveError(null);
    try {
      await httpsCallable(functions, 'adminUpdateDevice')({ deviceId: dev.deviceId, supplyTrial: on });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminUpdateDevice (supplyTrial) failed:', err);
      setSaveError(err?.message || 'Save failed.');
    } finally {
      setSavingField(null);
    }
  };

  // Website stock: an unclaimed unit counts unless this is off.
  const saveForSale = async (on) => {
    setSavingField('forSale');
    setSaveError(null);
    try {
      await httpsCallable(functions, 'adminUpdateDevice')({ deviceId: dev.deviceId, forSale: on });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminUpdateDevice (forSale) failed:', err);
      setSaveError(err?.message || 'Save failed.');
    } finally {
      setSavingField(null);
    }
  };

  // Comped on/off. The server works out leakDetection from it (comped OR a
  // live subscription), so refetch rather than guess.
  const saveComped = async (comped) => {
    setSavingField('plusComped');
    setSaveError(null);
    try {
      await httpsCallable(functions, 'adminUpdateDevice')({ deviceId: dev.deviceId, plusComped: comped });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminUpdateDevice (comped) failed:', err);
      setSaveError(err?.message || 'Save failed.');
    } finally {
      setSavingField(null);
    }
  };

  // Tank edits nest under tank1/tank2, and the callable derives emptyMa/fullMa
  // from the feet before writing so the pair can never drift. `warns` comes back
  // for soft checks (the "3 ft — did you mean inches?" one) and is shown rather
  // than swallowed: that is precisely the mistake this panel exists to fix.
  const saveTank = async (n, fields, fieldKey) => {
    setSavingField(fieldKey);
    setSaveError(null);
    setWarns([]);
    try {
      const call = httpsCallable(functions, 'adminUpdateDevice');
      const res = await call({ deviceId: dev.deviceId, [`tank${n}`]: fields });
      if (res.data?.warns?.length) setWarns(res.data.warns);
      // Refetch rather than patch locally: the server may have changed more
      // than we sent (the derived mA), so guessing here would show stale values.
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminUpdateDevice failed:', err);
      setSaveError(err?.message || 'Save failed.');
    } finally {
      setSavingField(null);
    }
  };

  const saveUserPrefs = async (uid, depthFormat) => {
    setSavingField(`units${uid}`);
    setSaveError(null);
    setWarns([]);
    try {
      await httpsCallable(functions, 'adminUpdateUserPrefs')({ uid, depthFormat });
      // The claim list lives on the device payload, so refetch rather than
      // trying to patch one nested account entry by hand.
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminUpdateUserPrefs failed:', err);
      setSaveError(err?.message || 'Could not change display units.');
    } finally {
      setSavingField(null);
    }
  };

  // Switch one account's monthly report on or off. Narrow by design: the
  // callable writes only alerts.monthlyRecap, so stopping someone's monthly
  // email can't disable their leak alerts by accident.
  const saveMonthlyRecap = async (uid, enabled) => {
    setSavingField(`recap${uid}`);
    setSaveError(null);
    setWarns([]);
    try {
      await httpsCallable(functions, 'adminSetMonthlyRecap')({ uid, deviceId: dev.deviceId, enabled });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminSetMonthlyRecap failed:', err);
      setSaveError(err?.message || 'Could not change the monthly report setting.');
    } finally {
      setSavingField(null);
    }
  };

  // Alerts belong to ONE account's claim on this device, so uid and deviceId
  // both travel. The server allowlists what may change (functions/adminAlerts.js)
  // and audits every call, so this just reports whatever it refuses.
  const saveAlerts = async (uid, deviceId, changes) => {
    setSavingField(`alerts${uid}`);
    setSaveError(null);
    setWarns([]);
    try {
      await httpsCallable(functions, 'adminUpdateAlerts')({ uid, deviceId, changes });
      onSaved(deviceId, null);
    } catch (err) {
      console.error('adminUpdateAlerts failed:', err);
      setSaveError(err?.message || 'Could not change those alert settings.');
    } finally {
      setSavingField(null);
    }
  };

  const doRelease = async () => {
    setReleaseBusy(true);
    setReleaseMsg(null);
    try {
      const res = await httpsCallable(functions, 'adminReleaseDevice')({
        deviceId: dev.deviceId,
        confirmDeviceId: releaseId.trim(),
        confirmOwnerEmail: releaseEmail.trim(),
      });
      const d = res?.data || {};
      setReleaseMsg({ ok: true, text: `Released. ${d.deletedClaims} account(s) and ${d.deletedPendingShares} pending invite(s) removed.` });
      setReleaseOpen(false);
      setReleaseId('');
      setReleaseEmail('');
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminReleaseDevice failed:', err);
      setReleaseMsg({ ok: false, text: err?.message || 'Could not release the device.' });
    } finally {
      setReleaseBusy(false);
    }
  };

  // Revoke access already granted. Shared users only — the owner's own claim is
  // refused server-side, since dropping it would leave a device nobody can
  // reach while still marked claimed.
  const removeShare = async (claim) => {
    const who = claim.email || claim.uid;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${who}'s access to ${dev.deviceId}?\n\nThey lose it immediately. The owner is not affected.`)) return;
    setSavingField(`revoke${claim.uid}`);
    setSaveError(null);
    try {
      const res = await httpsCallable(functions, 'adminRemoveShare')({ deviceId: dev.deviceId, uid: claim.uid });
      setShareMsg({
        ok: true,
        text: res?.data?.alreadyGone
          ? `${who} already had no access to this device.`
          : `Removed ${who}'s access.`,
      });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminRemoveShare failed:', err);
      setSaveError(err?.message || 'Could not remove that access.');
    } finally {
      setSavingField(null);
    }
  };

  // Withdraw an invite that will never be accepted — almost always a typo'd
  // address. Nothing is emailed: the recipient never had access to lose.
  const cancelInvite = async (pending) => {
    setCancelling(pending.id);
    setShareMsg(null);
    try {
      const res = await httpsCallable(functions, 'adminCancelPendingShare')({
        pendingId: pending.id, deviceId: dev.deviceId,
      });
      setShareMsg({
        ok: true,
        text: res?.data?.alreadyGone
          ? `That invite to ${pending.email} was already gone — they may have just accepted it.`
          : `Invite to ${pending.email} cancelled.`,
      });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminCancelPendingShare failed:', err);
      setShareMsg({ ok: false, text: err?.message || 'Could not cancel that invite.' });
    } finally {
      setCancelling(null);
    }
  };

  // Send a waiting invite's email again — for "she never got it" (usually spam
  // or deleted unread). The invite itself is unchanged.
  const resendInvite = async (pending) => {
    setResending(pending.id);
    setShareMsg(null);
    try {
      const res = await httpsCallable(functions, 'adminResendInvite')({
        pendingId: pending.id, deviceId: dev.deviceId,
      });
      setShareMsg({
        ok: true,
        text: res?.data?.alreadyGone
          ? `That invite to ${pending.email} is gone — they may have just accepted it.`
          : `Invite re-sent to ${pending.email}. Ask them to check spam too.`,
      });
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminResendInvite failed:', err);
      setShareMsg({ ok: false, text: err?.message || 'Could not resend that invite.' });
    } finally {
      setResending(null);
    }
  };

  // Set up a share on the owner's behalf — the case this is for is an elderly
  // owner on the phone who wants their daughter to see the tank and can't work
  // the sharing screen. The owner gets an email saying who was added.
  const sendShare = async () => {
    const email = shareEmail.trim();
    if (!email) return;
    setShareBusy(true);
    setShareMsg(null);
    setSaveError(null);
    try {
      const res = await httpsCallable(functions, 'adminShareDevice')({ deviceId: dev.deviceId, recipientEmail: email });
      const d = res?.data || {};
      setShareMsg(d.alreadyShared
        ? { ok: true, text: `${email} already has access — nothing sent.` }
        : { ok: true, text: d.recipientExists
            ? `Shared with ${email}. They'll see it next time they sign in.`
            : `Invite sent to ${email}. It becomes active when they make an account.` });
      setShareEmail('');
      onSaved(dev.deviceId, null);
    } catch (err) {
      console.error('adminShareDevice failed:', err);
      setShareMsg({ ok: false, text: err?.message || 'Could not share the device.' });
    } finally {
      setShareBusy(false);
    }
  };

  if (!dev) return null;

  const curCadence = dev.steadyPollSec || 30;
  const cadenceOptions = Array.from(new Set([...CADENCE_PRESETS, curCadence]))
    .sort((a, b) => a - b)
    .map(v => ({ v, l: cadenceLabel(v) }));

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="admin-modal__head">
          <div>
            <div className="admin-modal__title">
              <StatusDot online={dev.online} />
              {dev.name || 'Unnamed'}
            </div>
            <div className="admin-modal__sub">
              {dev.deviceId}
              {' · '}
              {dev.online ? 'Online' : `Last seen ${timeSince(dev.lastSeenMs)}`}
            </div>
          </div>
          <button type="button" className="admin-modal__x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="admin-modal__body">
          {/* Setup / device facts */}
          <section className="admin-section">
            <h4>Device</h4>
            <div className="admin-facts">
              <div><span>Firmware</span><b>{dev.fw || '—'}</b></div>
              <div><span>Model</span><b>{dev.model || '—'}</b></div>
              <EditableFact
                label="Device name" value={dev.name || ''} saving={savingField === 'name'}
                onSave={(v) => save({ name: v == null ? '' : v }, 'name')}
              />
              <div className="admin-facts__wide">
                <span>LED mode</span>
                <b>
                  <span className="admin-select-wrap">
                    <select
                      className="admin-sort admin-facts__inline-select"
                      value={dev.ledMode || 'always_on'}
                      disabled={savingField === 'ledMode'}
                      onChange={(e) => save({ ledMode: e.target.value }, 'ledMode')}
                    >
                      {LED_MODE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <IconChevronDown />
                  </span>
                </b>
              </div>
              <div><span>Claimed</span><b>{dev.claimed ? 'Yes' : 'No'}</b></div>
              <div><span>Last reading</span><b>{fmtDateTime(dev.reading?.tsMs)}</b></div>
              <div><span>Last seen</span><b>{fmtDateTime(dev.lastSeenMs)}</b></div>
              <div><span>Uptime (since install)</span><b>{fmtUptime(uptime, 'uptimePct')}</b></div>
              <div><span>Uptime (24h)</span><b>{fmtUptime(uptime, 'uptime24hPct')}</b></div>
            </div>

            {/* Editable operator controls — admin-only, written via the
                adminUpdateDevice callable (Firestore rules unchanged). */}
            <div className="admin-edit">
              <div className="admin-edit__row">
                <div className="admin-edit__label">
                  Plus
                  <span className="admin-edit__hint">
                    {plusAdminLine(dev.plus)}
                    {' · '}leak protection {dev.leakDetection ? 'on' : 'off'}
                    {dev.tier === 'free' && ' · Free: reporting hourly, daily view'}
                    {dev.tier === 'free-grace' && ' · Free, first 24 h at full speed'}
                  </span>
                </div>
              </div>
              <div className="admin-edit__row">
                <div className="admin-edit__label">
                  Comped
                  <span className="admin-edit__hint">Plus at no charge. Stripe never turns it off.</span>
                </div>
                <button
                  type="button"
                  className={`admin-toggle ${dev.plus?.comped ? 'admin-toggle--on' : ''}`}
                  disabled={savingField === 'plusComped'}
                  aria-pressed={dev.plus?.comped === true}
                  onClick={() => saveComped(!(dev.plus?.comped === true))}
                >
                  <span className="admin-toggle__knob" />
                  <span className="admin-toggle__text">{dev.plus?.comped ? 'On' : 'Off'}</span>
                </button>
              </div>
              <div className="admin-edit__row">
                <div className="admin-edit__label">
                  Time until empty (trial)
                  <span className="admin-edit__hint">Shows the estimate on this monitor&apos;s tank cards. Off for everyone else.</span>
                </div>
                <button
                  type="button"
                  className={`admin-toggle ${dev.supplyTrial ? 'admin-toggle--on' : ''}`}
                  disabled={savingField === 'supplyTrial'}
                  aria-pressed={dev.supplyTrial === true}
                  onClick={() => saveSupplyTrial(!(dev.supplyTrial === true))}
                >
                  <span className="admin-toggle__knob" />
                  <span className="admin-toggle__text">{dev.supplyTrial ? 'On' : 'Off'}</span>
                </button>
              </div>
              {!dev.claimed && (
                <div className="admin-edit__row">
                  <div className="admin-edit__label">
                    For sale
                    <span className="admin-edit__hint">{dev.orderId ? 'On a website order (Orders tab).' : 'Counts as website stock while unclaimed. Turn off for bench and test units.'}</span>
                  </div>
                  <button
                    type="button"
                    className={`admin-toggle ${dev.forSale !== false ? 'admin-toggle--on' : ''}`}
                    disabled={savingField === 'forSale'}
                    aria-pressed={dev.forSale !== false}
                    onClick={() => saveForSale(!(dev.forSale !== false))}
                  >
                    <span className="admin-toggle__knob" />
                    <span className="admin-toggle__text">{dev.forSale !== false ? 'On' : 'Off'}</span>
                  </button>
                </div>
              )}

              <div className="admin-edit__row">
                <div className="admin-edit__label">
                  Poll cadence
                  <span className="admin-edit__hint">How often it reports. Faster = more data &amp; more usage.</span>
                </div>
                <select
                  className="admin-sort admin-edit__select"
                  value={curCadence}
                  disabled={savingField === 'steadyPollSec'}
                  onChange={(e) => save({ steadyPollSec: Number(e.target.value) }, 'steadyPollSec')}
                >
                  {cadenceOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>

              {savingField && <div className="admin-edit__status">Saving…</div>}
              {saveError && <div className="admin-edit__status admin-edit__status--err">{saveError}</div>}
              {warns.map((w) => (
                <div key={w} className="admin-edit__status admin-edit__status--warn">{w}</div>
              ))}
              <div className="admin-edit__note">
                Takes effect on the device&apos;s next poll.
                {dev.tier === 'free' && ' Free monitors report hourly. This setting applies with Plus.'}
              </div>
            </div>
          </section>

          {/* Live reading */}
          <section className="admin-section">
            <h4>Latest reading</h4>
            <div className="admin-facts">
              <div><span>Tank 1</span><b>{dev.reading?.p1 != null ? `${Math.round(dev.reading.p1)}%` : '—'}{dev.reading?.f1 ? ' ⚠ fault' : ''}</b></div>
              <div><span>Tank 1 gallons</span><b>{fmtGal(dev.tanks[0]?.gallons)}</b></div>
              <div><span>Tank 1 sensor height</span><b>{fmtSensorIn(dev.tanks[0]?.sensorFt)}</b></div>
              <div><span>Tank 1 mA</span><b>{dev.reading?.mA1 != null ? dev.reading.mA1.toFixed(2) : '—'}</b></div>
              {dev.reading?.s2 && <div><span>Tank 2</span><b>{dev.reading?.p2 != null ? `${Math.round(dev.reading.p2)}%` : '—'}{dev.reading?.f2 ? ' ⚠ fault' : ''}</b></div>}
              {dev.reading?.s2 && <div><span>Tank 2 gallons</span><b>{fmtGal(dev.tanks[1]?.gallons)}</b></div>}
              {dev.reading?.s2 && <div><span>Tank 2 sensor height</span><b>{fmtSensorIn(dev.tanks[1]?.sensorFt)}</b></div>}
              {dev.reading?.s2 && <div><span>Tank 2 mA</span><b>{dev.reading?.mA2 != null ? dev.reading.mA2.toFixed(2) : '—'}</b></div>}
            </div>
          </section>

          {/* Calibration / tanks */}
          <section className="admin-section">
            <h4>Calibration &amp; setup</h4>
            {dev.tanks.length === 0 && <div className="admin-kv__muted">No tank configuration on record.</div>}
            {dev.tanks.map((t) => (
              <div className="admin-tankcard" key={t.n}>
                <div className="admin-tankcard__name">{t.name || `Tank ${t.n}`}</div>
                <div className="admin-facts admin-facts--tight">
                  <EditableFact
                    label="Tank name" value={t.name || ''} saving={savingField === `t${t.n}name`}
                    onSave={(v) => saveTank(t.n, { name: v == null ? '' : v }, `t${t.n}name`)}
                  />
                  <EditableFact
                    label="Capacity" value={t.capacity} suffix=" gal" type="number"
                    saving={savingField === `t${t.n}cap`}
                    onSave={(v) => saveTank(t.n, { capacity: v }, `t${t.n}cap`)}
                  />
                  <EditableFact
                    label="Empty ft" value={t.emptyFt} type="number"
                    hint="In FEET. 3 inches = 0.25"
                    saving={savingField === `t${t.n}empty`}
                    onSave={(v) => saveTank(t.n, { emptyFt: v }, `t${t.n}empty`)}
                  />
                  <EditableFact
                    label="Full ft" value={t.fullFt} type="number"
                    hint="In FEET, measured from the sensor."
                    saving={savingField === `t${t.n}full`}
                    onSave={(v) => saveTank(t.n, { fullFt: v }, `t${t.n}full`)}
                  />
                  <div><span>Cal points</span><b>{t.calPoints}</b></div>
                  {/* mA are DERIVED from the feet above, server-side, and written
                      as a pair. Editing one by hand is exactly what desyncs them,
                      so they stay read-only here. */}
                  <div><span>Empty mA</span><b>{t.emptyMa != null ? t.emptyMa : '—'}</b></div>
                  <div><span>Full mA</span><b>{t.fullMa != null ? t.fullMa : '—'}</b></div>
                </div>

                {/* Factory calibration archive — shown as its own data points
                    alongside the current cal above. */}
                <div className="admin-tankcard__sub">Factory calibration{t.factory?.source ? ` · ${t.factory.source}` : ''}</div>
                {t.factory ? (
                  <div className="admin-facts admin-facts--tight">
                    <div><span>Full-scale</span><b>{t.factory.fullScaleFt != null ? `${t.factory.fullScaleFt} ft` : '—'}</b></div>
                    <div><span>Slope</span><b>{t.factory.slope != null ? `${t.factory.slope} mA/ft` : '—'}</b></div>
                    <div><span>Intercept</span><b>{t.factory.intercept != null ? `${t.factory.intercept} mA` : '—'}</b></div>
                    <div><span>Cal points</span><b>{t.factory.pointCount}</b></div>
                    <div><span>Firmware</span><b>{t.factory.fw || '—'}</b></div>
                    <div><span>Captured</span><b>{t.factory.capturedAtMs ? fmtDateTime(t.factory.capturedAtMs) : '—'}</b></div>
                  </div>
                ) : (
                  <div className="admin-kv__muted">No factory calibration on record.</div>
                )}
              </div>
            ))}

            {/* Repeated here on purpose: a tank edit that fails or warns must
                report next to the field that caused it, not only in the Device
                section at the top of a scrolling modal. */}
            <div className="admin-edit">
              {savingField && <div className="admin-edit__status">Saving…</div>}
              {saveError && <div className="admin-edit__status admin-edit__status--err">{saveError}</div>}
              {warns.map((w) => (
                <div key={w} className="admin-edit__status admin-edit__status--warn">{w}</div>
              ))}
              <div className="admin-edit__note">
                Empty/Full mA are derived from the feet above and saved together.
                Changes reach the device on its next poll.
              </div>
            </div>
          </section>

          {/* Accounts / claims */}
          <section className="admin-section">
            <h4>Accounts ({dev.claims.length})</h4>
            {dev.claims.length === 0 && <div className="admin-kv__muted">Unclaimed — no accounts have access.</div>}
            {dev.claims.map((c) => (
              <div className="admin-account" key={c.uid}>
                <div className="admin-account__head">
                  <span className={`admin-role admin-role--${c.role === 'owner' ? 'owner' : 'shared'}`}>{c.role || 'member'}</span>
                  <span className="admin-account__email">{c.email || '(no email)'}</span>
                </div>
                <div className="admin-facts admin-facts--tight">
                  {c.displayName && <div><span>Name</span><b>{c.displayName}</b></div>}
                  <div><span>Phone</span><b>{c.phone ? `${c.phone}${c.phoneVerified ? ' ✓' : ' (unverified)'}` : '—'}</b></div>
                  <div className="admin-facts__wide">
                    <span>Display units</span>
                    <b>
                      <span className="admin-select-wrap">
                        <select
                          className="admin-sort admin-facts__inline-select"
                          value={c.depthFormat || 'ft-in'}
                          disabled={savingField === `units${c.uid}`}
                          onChange={(e) => saveUserPrefs(c.uid, e.target.value)}
                        >
                          {DEPTH_FORMAT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        <IconChevronDown />
                      </span>
                    </b>
                  </div>
                  <div className="admin-facts__wide">
                    <span>Monthly report</span>
                    <b>
                      <span className="admin-select-wrap">
                        <select
                          className="admin-sort admin-facts__inline-select"
                          value={(c.alerts?.monthlyRecap === true || c.alerts?.monthlyRecap?.on === true) ? 'on' : 'off'}
                          disabled={savingField === `recap${c.uid}`}
                          onChange={(e) => saveMonthlyRecap(c.uid, e.target.value === 'on')}
                        >
                          <option value="on">Receiving</option>
                          <option value="off">Not receiving</option>
                        </select>
                        <IconChevronDown />
                      </span>
                      {/* The report also needs email alerts on; say so here so
                          switching it to "Receiving" doesn't look broken. */}
                      {!(c.alerts?.enabled === true) && (
                        <span className="admin-kv__muted" style={{ marginLeft: 8 }}>
                          email alerts off
                        </span>
                      )}
                    </b>
                  </div>
                  <div className="admin-facts__wide"><span>UID</span><b className="admin-mono">{c.uid}</b></div>
                </div>
                {c.role !== 'owner' && (
                  <div className="admin-account__revoke">
                    <button type="button" className="admin-rule-remove"
                      disabled={savingField === `revoke${c.uid}`}
                      onClick={() => removeShare(c)}>
                      {savingField === `revoke${c.uid}` ? 'Removing…' : 'Remove this person\u2019s access'}
                    </button>
                  </div>
                )}
                <AlertsPanel
                  claim={c}
                  deviceId={dev.deviceId}
                  tanks={dev.tanks}
                  busy={savingField === `alerts${c.uid}`}
                  onChange={(changes) => saveAlerts(c.uid, dev.deviceId, changes)}
                />
              </div>
            ))}
            <div className="admin-share">
              <div className="admin-account__alerts-label">Share with someone else</div>
              <div className="admin-share__row">
                <input
                  type="email"
                  className="admin-share__input"
                  placeholder="family@example.com"
                  value={shareEmail}
                  disabled={shareBusy || !dev.claimed}
                  onChange={(e) => { setShareEmail(e.target.value); setShareMsg(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendShare(); }}
                />
                <button type="button" className="btn btn-primary admin-share__btn"
                  disabled={shareBusy || !shareEmail.trim() || !dev.claimed}
                  onClick={sendShare}>
                  {shareBusy ? 'Sending…' : 'Send invite'}
                </button>
              </div>
              {!dev.claimed && (
                <div className="admin-kv__muted">Nobody owns this device yet, so there is nothing to share.</div>
              )}
              {shareMsg && (
                <div className={shareMsg.ok ? 'admin-share__ok' : 'admin-share__err'}>{shareMsg.text}</div>
              )}
              <div className="admin-alert-foot">
                The owner gets an email naming who was added and how to undo it.
              </div>

              {/* Invites that have not landed yet. One of these sitting here
                  for days is the answer to "I sent it to my daughter and
                  nothing happened" — she has not made an account, so there is
                  no claim to grant and nothing on her end to see. */}
              {dev.pendingShares && dev.pendingShares.length > 0 && (
                <div className="admin-pending">
                  <div className="admin-account__alerts-label">
                    Invites waiting ({dev.pendingShares.length})
                  </div>
                  {dev.pendingShares.map((p) => (
                    <div className="admin-pending__row" key={p.id}>
                      <span className="admin-pending__email">{p.email || '(no address)'}</span>
                      <span className="admin-pending__when">
                        {p.createdAt ? `sent ${timeSince(p.createdAt)}` : 'sent — date unknown'}
                        {p.lastResentAt ? ` · re-sent ${timeSince(p.lastResentAt)}` : ''}
                      </span>
                      <button type="button" className="admin-rule-add"
                        disabled={resending === p.id || cancelling === p.id || !p.email}
                        aria-label={`Resend invite to ${p.email || 'this address'}`}
                        onClick={() => resendInvite(p)}>
                        {resending === p.id ? 'Sending…' : 'Resend'}
                      </button>
                      <button type="button" className="admin-rule-remove"
                        disabled={cancelling === p.id || resending === p.id}
                        aria-label={`Cancel invite to ${p.email || 'this address'}`}
                        onClick={() => cancelInvite(p)}>
                        {cancelling === p.id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    </div>
                  ))}
                  <div className="admin-alert-foot">
                    These become real access the moment each person creates an account
                    with that same address. Until then there is nothing on their end.
                  </div>
                </div>
              )}
            </div>
            {/* Release — the only thing here that cannot be undone from this
                console. Collapsed behind a link, then two values that must be
                read off the record above and typed. Neither is a secret; the
                point is that releasing the wrong device takes a deliberate
                mistake rather than one misplaced click, and that a stale page
                cannot release a device that has since changed hands. */}
            {dev.claimed && (
              <div className="admin-danger">
                {!releaseOpen ? (
                  <button type="button" className="admin-danger__open"
                    onClick={() => { setReleaseOpen(true); setReleaseMsg(null); }}>
                    Release this device…
                  </button>
                ) : (
                  <>
                    <div className="admin-danger__title">Release {dev.deviceId}</div>
                    <p className="admin-danger__text">
                      Deletes every account&apos;s access, owner included, and clears the
                      device name. The customer would have to claim it again with the
                      original QR code. Pending invites are deleted too.
                    </p>
                    <label className="admin-danger__label">
                      Type the device ID
                      <input type="text" className="admin-share__input" autoComplete="off"
                        placeholder={dev.deviceId} value={releaseId}
                        onChange={(e) => setReleaseId(e.target.value)} />
                    </label>
                    <label className="admin-danger__label">
                      Type the owner&apos;s email
                      <input type="text" className="admin-share__input" autoComplete="off"
                        placeholder={dev.ownerEmail || '(no owner email on record)'}
                        value={releaseEmail}
                        onChange={(e) => setReleaseEmail(e.target.value)} />
                    </label>
                    <div className="admin-share__row">
                      <button type="button" className="admin-danger__go"
                        disabled={releaseBusy
                          || releaseId.trim().toUpperCase() !== dev.deviceId
                          || releaseEmail.trim().toLowerCase() !== String(dev.ownerEmail || '').toLowerCase()
                          || !dev.ownerEmail}
                        onClick={doRelease}>
                        {releaseBusy ? 'Releasing…' : 'Release device'}
                      </button>
                      <button type="button" className="admin-rule-add" disabled={releaseBusy}
                        onClick={() => { setReleaseOpen(false); setReleaseId(''); setReleaseEmail(''); }}>
                        Cancel
                      </button>
                    </div>
                    {releaseMsg && (
                      <div className={releaseMsg.ok ? 'admin-share__ok' : 'admin-share__err'}>{releaseMsg.text}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ── Orders ─────────────────────────────────────────────────────────────────
//
// Website orders (Stripe Checkout). Read and changed through admin-claim-gated
// callables (adminOrders, adminOrderAction); orders/ is denied to every client.
// A paid order waits to go out. Mark it shipped (a tracking number emails the
// customer) or picked up, choosing the unit in the box: that links the unit to
// the order, so when it's set up its included year of SoKo Plus starts on the
// buyer's card. Undo fixes a wrong pick until the unit is set up.

const ORDER_MODEL = { single: 'Single-tank', dual: 'Dual-tank' };
const fmtOrderDate = (ms) => (ms
  ? new Date(ms).toLocaleString('en-US', { timeZone: 'Pacific/Honolulu', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');
const fmtMoney = (c) => (typeof c === 'number' ? `$${(c / 100).toFixed(2).replace(/\.00$/, '')}` : '');

const OTHER_UNIT = '__other';

function orderChip(o) {
  const pickup = o.delivery === 'pickup';
  if (o.status === 'paid') return { label: pickup ? 'To hand over' : 'To ship', cls: 'admin-chip--copper' };
  if (o.status === 'assigned') return { label: `${o.deviceId} · ${pickup ? 'to hand over' : 'to ship'}`, cls: 'admin-chip--copper' };
  if (o.status === 'shipped') return { label: `${o.deviceId} · ${pickup ? 'picked up' : 'shipped'} ${fmtOrderDate(o.shippedAtMs).split(',')[0]}`, cls: 'admin-chip--reef' };
  if (o.status === 'active') return { label: `${o.deviceId} · set up ${fmtOrderDate(o.activatedAtMs).split(',')[0]}`, cls: '' };
  return { label: o.status, cls: '' };
}

function OrderRow({ o, units, onDone }) {
  const [pick, setPick] = useState('');
  const [otherId, setOtherId] = useState('');
  const [tracking, setTracking] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const pickup = o.delivery === 'pickup';
  const free = units.filter((u) => u.model === o.model && u.forSale && !u.orderId);
  const toGo = o.status === 'paid' || o.status === 'assigned';
  const unitId = o.status === 'assigned' ? o.deviceId : (pick === OTHER_UNIT ? otherId.trim().toUpperCase() : pick);
  // Undo while the unit is still unclaimed (it's in the unclaimed list).
  const canUndo = o.status === 'shipped' && units.some((u) => u.deviceId === o.deviceId);
  const act = async (payload, noteFor) => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const res = await httpsCallable(functions, 'adminOrderAction')({ orderId: o.id, ...payload });
      if (noteFor) setNote(noteFor(res?.data || {}));
      await onDone();
    } catch (e) {
      setErr(e?.message || 'That didn\u2019t work.');
    } finally {
      setBusy(false);
    }
  };
  const ship = () => act({ action: 'ship', deviceId: unitId, tracking }, (r) => (r.emailed ? 'Tracking emailed to the customer.' : null));
  const undo = () => {
    if (window.confirm(`Undo ${pickup ? 'picked up' : 'shipped'}? ${o.deviceId} goes back on sale and this order waits again.`)) act({ action: 'undo' });
  };
  const chip = orderChip(o);
  const where = pickup
    ? `Local pickup${o.phone ? ` · ${o.phone}` : ''}`
    : (o.shipTo ? `Ships to ${[o.shipTo.city, o.shipTo.state].filter(Boolean).join(', ')} ${o.shipTo.postalCode || ''}` : 'Ships');
  return (
    <div className="card admin-row">
      <div className="admin-row__header">
        <div className="admin-row__name">{o.name || o.email || 'Customer'} · {ORDER_MODEL[o.model] || o.model}</div>
        <span className={`admin-chip ${chip.cls}`}>{chip.label}</span>
      </div>
      <div className="admin-row__meta">
        Paid {fmtOrderDate(o.createdAtMs)} · {fmtMoney(o.amountTotal)} · SoKo Plus {o.plan === 'monthly' ? 'monthly' : 'yearly'} · {where}
        {o.email ? <><br />{o.email}</> : null}
        {o.delivery === 'ship' && o.shipTo && (o.shipTo.name || o.shipTo.line1)
          ? <><br />{[o.shipTo.name, o.shipTo.line1, o.shipTo.line2].filter(Boolean).join(', ')}</> : null}
        {o.tracking ? <><br />Tracking {o.tracking}</> : null}
      </div>
      {toGo && (
        <div className="admin-controls" style={{ marginTop: 10, marginBottom: 0, flexWrap: 'wrap' }}>
          {o.status === 'paid' && (
            <select className="admin-sort" value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy} aria-label="Unit in the box">
              <option value="">{free.length ? 'Unit in the box…' : 'No units for sale'}</option>
              {free.map((u) => <option key={u.deviceId} value={u.deviceId}>{u.deviceId}</option>)}
              <option value={OTHER_UNIT}>Already set up by the customer…</option>
            </select>
          )}
          {o.status === 'paid' && pick === OTHER_UNIT && (
            <input className="admin-search" placeholder="Device ID, e.g. WT-0012" value={otherId}
              onChange={(e) => setOtherId(e.target.value)} disabled={busy} />
          )}
          {!pickup && (
            <input className="admin-search" placeholder="Tracking number" value={tracking}
              onChange={(e) => setTracking(e.target.value)} disabled={busy} />
          )}
          <button type="button" className="btn btn-primary" disabled={!unitId || busy} onClick={ship}>
            {pickup ? 'Mark picked up' : 'Mark shipped'}
          </button>
          {o.status === 'assigned' && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={undo}>Undo {o.deviceId}</button>
          )}
        </div>
      )}
      {canUndo && (
        <div className="admin-controls" style={{ marginTop: 10, marginBottom: 0 }}>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={undo}>Wrong unit? Undo</button>
        </div>
      )}
      {err && <div className="admin-row__meta" style={{ color: 'var(--color-copper)', marginTop: 8 }}>{err}</div>}
      {note && <div className="admin-row__meta" style={{ marginTop: 8 }}>{note}</div>}
    </div>
  );
}

function OrdersPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await httpsCallable(functions, 'adminOrders')();
      setData(res.data);
    } catch (err) {
      console.error('adminOrders failed:', err);
      setError(err?.message || 'Could not load orders.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  const orders = data?.orders || [];
  const units = data?.units || [];
  const waiting = orders.filter((o) => o.status === 'paid' || o.status === 'assigned').length;
  return (
    <>
      {data?.mode === 'test' && (
        <div className="alert-banner info" style={{ marginBottom: 12 }}>
          <div className="alert-banner-desc">Stripe test mode: these are test orders.</div>
        </div>
      )}
      <div className="admin-summary">
        <div className="card admin-summary__card">
          <div className="admin-summary__num">{data?.stock?.single ?? 0}</div>
          <div className="admin-summary__label">Single-tank in stock</div>
        </div>
        <div className="card admin-summary__card">
          <div className="admin-summary__num">{data?.stock?.dual ?? 0}</div>
          <div className="admin-summary__label">Dual-tank in stock</div>
        </div>
        <div className="card admin-summary__card">
          <div className={`admin-summary__num ${waiting ? 'admin-summary__num--copper' : ''}`}>{waiting}</div>
          <div className="admin-summary__label">Waiting to go out</div>
        </div>
      </div>
      {error && (
        <div className="alert-banner warn" style={{ marginBottom: 12 }}>
          <div className="alert-banner-desc">{error}</div>
        </div>
      )}
      <div className="admin-list">
        {orders.length === 0 && <div className="admin-empty">No orders yet.</div>}
        {orders.map((o) => <OrderRow key={o.id} o={o} units={units} onDone={load} />)}
      </div>
      <div className="admin-summary__label" style={{ margin: '18px 0 8px' }}>Unclaimed units</div>
      <div className="admin-list">
        {units.length === 0 && <div className="admin-empty">No unclaimed units. Flashed units show up here.</div>}
        {units.map((u) => (
          <div key={u.deviceId} className="card admin-row">
            <div className="admin-row__header">
              <div className="admin-row__name">{u.deviceId} · {ORDER_MODEL[u.model]}</div>
              <span className={`admin-chip ${u.orderId ? 'admin-chip--reef' : ''}`}>
                {u.orderId ? 'On an order' : u.forSale ? 'For sale' : 'Not for sale'}
              </span>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-secondary" onClick={() => { setLoading(true); load(); }} style={{ maxWidth: '560px', marginTop: 12 }}>
        Refresh
      </button>
    </>
  );
}

// ── Waitlist ───────────────────────────────────────────────────────────────
//
// The product waitlist from the marketing site's "notify me" form. Read and
// written through admin-claim-gated callables — the collection itself is denied
// to every client by firestore.rules, so there is no direct Firestore path here.
//
// The number that matters is "waiting": it sizes the next batch. The weekly
// counts below it show whether demand is rising, which is the cue to order
// parts ahead rather than react.

const WAITLIST_STATUS_LABELS = {
  waiting: 'Waiting', contacted: 'Contacted', sold: 'Sold', declined: 'Declined',
};

function WaitlistPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('waiting');
  const [saving, setSaving]   = useState(null);
  const [copied, setCopied]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await httpsCallable(functions, 'adminWaitlist')();
      setData(res.data || null);
    } catch (err) {
      console.error('adminWaitlist failed:', err);
      setError(err?.message || 'Failed to load the waitlist.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Optimistic: reflect the change immediately, roll back if the call fails.
  const update = useCallback(async (id, patch) => {
    setSaving(id);
    const before = data;
    setData(prev => prev && ({
      ...prev,
      entries: prev.entries.map(e => (e.id === id ? { ...e, ...patch } : e)),
    }));
    try {
      await httpsCallable(functions, 'adminUpdateWaitlist')({ id, ...patch });
      await load();
    } catch (err) {
      console.error('adminUpdateWaitlist failed:', err);
      setData(before);
      setError(err?.message || 'Could not save that change.');
    } finally { setSaving(null); }
  }, [data, load]);

  const entries = data?.entries || [];
  const shown = filter === 'all' ? entries : entries.filter(e => e.status === filter);

  const copyWaiting = async () => {
    const list = entries.filter(e => e.status === 'waiting').map(e => e.email).join(', ');
    try {
      await navigator.clipboard.writeText(list);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setError('Clipboard blocked — select the addresses manually.'); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  const counts = data?.counts || {};
  const weekly = data?.weekly || [];
  const peak = Math.max(1, ...weekly.map(w => w.count));

  return (
    <>
      <div className="admin-summary">
        <div className="card admin-summary__card">
          <div className="admin-summary__num admin-summary__num--copper">{counts.waiting || 0}</div>
          <div className="admin-summary__label">Waiting</div>
        </div>
        <div className="card admin-summary__card">
          <div className="admin-summary__num">{counts.contacted || 0}</div>
          <div className="admin-summary__label">Contacted</div>
        </div>
        <div className="card admin-summary__card">
          <div className="admin-summary__num admin-summary__num--reef">{counts.sold || 0}</div>
          <div className="admin-summary__label">Sold</div>
        </div>
        <div className="card admin-summary__card">
          <div className="admin-summary__num">{data?.total || 0}</div>
          <div className="admin-summary__label">All time</div>
        </div>
      </div>

      {/* Signups per week, newest on the right. Sizes the next build. */}
      <div className="card" style={{ padding: '16px 18px', marginBottom: 12 }}>
        <div className="admin-summary__label" style={{ marginBottom: 12 }}>Signups per week</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 64 }}>
          {weekly.slice().reverse().map((w) => (
            <div key={w.startMs} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>{w.count}</div>
              <div style={{
                height: `${Math.round((w.count / peak) * 40)}px`,
                minHeight: 2, borderRadius: 3, background: 'var(--kona-reef, #2E7A7F)',
                opacity: w.count ? 1 : 0.25,
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6, opacity: 0.65 }}>
          <span>8 weeks ago</span><span>this week</span>
        </div>
      </div>

      <div className="admin-controls">
        <select value={filter} onChange={e => setFilter(e.target.value)} className="admin-sort">
          <option value="waiting">Waiting</option>
          <option value="contacted">Contacted</option>
          <option value="sold">Sold</option>
          <option value="declined">Declined</option>
          <option value="all">All</option>
        </select>
        <button type="button" className="btn btn-secondary" onClick={copyWaiting}
          disabled={!(counts.waiting > 0)}>
          {copied ? 'Copied' : `Copy ${counts.waiting || 0} waiting`}
        </button>
      </div>

      {error && (
        <div className="alert-banner warn" style={{ marginBottom: 12 }}>
          <div className="alert-banner-desc">{error}</div>
        </div>
      )}

      <div className="admin-list">
        {shown.length === 0 && <div className="admin-empty">Nobody here yet.</div>}
        {shown.map(e => (
          <div key={e.id} className="card admin-row">
            <div className="admin-row__header">
              <div className="admin-row__name">{e.email}</div>
            </div>
            <div className="admin-row__meta">
              <span className="admin-row__email">
                {e.createdAtMs ? new Date(e.createdAtMs).toLocaleDateString() : '—'}
              </span>
              {e.source && e.source !== 'website' && (
                <span className="admin-chip">{e.source}</span>
              )}
            </div>
            <div className="admin-controls" style={{ marginTop: 10, marginBottom: 0 }}>
              <select
                value={e.status}
                disabled={saving === e.id}
                onChange={ev => update(e.id, { status: ev.target.value })}
                className="admin-sort"
              >
                {Object.entries(WAITLIST_STATUS_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
              <input
                className="admin-search"
                defaultValue={e.note}
                placeholder="Note (wants 2, dual tank…)"
                disabled={saving === e.id}
                onBlur={ev => {
                  if (ev.target.value !== e.note) update(e.id, { note: ev.target.value });
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="btn btn-secondary" onClick={load} style={{ maxWidth: '560px' }}>
        Refresh
      </button>
    </>
  );
}

export default function Admin({ user }) {
  const [devices, setDevices] = useState([]);
  const [meta, setMeta]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [sortBy, setSortBy]   = useState('lastSeen');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('devices');

  // AdminRoute already resolved the admin CLAIM before rendering this page, so
  // reaching here means the server-side gate passed. This stays only as a guard
  // against the component being mounted some other way; it must not re-derive
  // admin from an email string, which disagrees with the claim the moment the
  // account's email changes (and every admin callable checks the claim anyway).
  const isAdmin = !!user;

  const loadAll = useCallback(async (opts = {}) => {
    if (!opts.quiet) setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'adminOverview');
      const res = await call();
      const data = res.data || {};
      const list = Array.isArray(data.devices) ? data.devices : [];
      setDevices(list);
      setMeta(data);
      // Keep an open detail modal pointed at the freshly-loaded object.
      setSelected(prev => (prev ? (list.find(d => d.deviceId === prev.deviceId) || prev) : prev));
    } catch (err) {
      console.error('adminOverview failed:', err);
      setError(err?.message || 'Failed to load. Your account may not have admin access yet.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply a simple edit locally so the row and the open modal update at once.
  // A NULL patch means "the server changed more than we sent" -- tank edits
  // derive emptyMa/fullMa server-side, so guessing locally would paint stale
  // numbers. Refetch quietly instead, without flashing the loading spinner.
  const handleSaved = useCallback((deviceId, patch) => {
    if (patch === null) { loadAll({ quiet: true }); return; }
    setDevices(prev => prev.map(d => (d.deviceId === deviceId ? { ...d, ...patch } : d)));
    setSelected(prev => (prev && prev.deviceId === deviceId ? { ...prev, ...patch } : prev));
  }, [loadAll]);

  useEffect(() => {
    if (!isAdmin || tab !== 'devices') return;
    loadAll();
  }, [isAdmin, tab, loadAll]);

  const list = useMemo(() => {
    let out = devices.slice();

    if (statusFilter === 'online')    out = out.filter(d => d.online);
    else if (statusFilter === 'offline')   out = out.filter(d => !d.online);
    else if (statusFilter === 'unclaimed') out = out.filter(d => !d.claimed);
    else if (statusFilter === 'leak')      out = out.filter(d => d.leakDetection);
    else if (statusFilter === 'fault')     out = out.filter(d => d.reading?.f1 || d.reading?.f2);

    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(d =>
        (d.deviceId || '').toLowerCase().includes(s) ||
        (d.name || '').toLowerCase().includes(s) ||
        (d.ownerEmail || '').toLowerCase().includes(s) ||
        (d.claims || []).some(c => (c.email || '').toLowerCase().includes(s))
      );
    }

    out.sort((a, b) => {
      if (sortBy === 'lastSeen') return b.lastSeenMs - a.lastSeenMs;
      if (sortBy === 'id')       return (a.deviceId || '').localeCompare(b.deviceId || '');
      if (sortBy === 'name')     return (a.name || '~').localeCompare(b.name || '~');
      if (sortBy === 'status')   return (b.online ? 1 : 0) - (a.online ? 1 : 0);
      if (sortBy === 'level')    return (b.reading?.p1 ?? -1) - (a.reading?.p1 ?? -1);
      return 0;
    });
    return out;
  }, [devices, statusFilter, search, sortBy]);

  const totalAll     = meta?.deviceCount ?? devices.length;
  const totalOnline  = meta?.onlineCount ?? devices.filter(d => d.online).length;
  const totalOffline = totalAll - totalOnline;
  const totalUsers   = meta?.userCount ?? 0;
  const alerts24h    = meta?.alertsLast24h ?? 0;
  const fleetAlerts  = useMemo(() => latestAcrossFleet(devices, 5), [devices]);
  const [alertsOpen, setAlertsOpen] = useState(false);

  if (!isAdmin) {
    return (
      <div className="app-container">
        <TopBar subtitle="Admin" />
        <div className="page-content"><div className="admin-empty">Not authorized.</div></div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-container">
      <TopBar
        subtitle="Admin"
        rightContent={<span className="admin-online-count">{totalOnline}/{totalAll} online</span>}
      />
      <div className="page-content">

        {/* Tabs */}
        <div className="admin-controls" role="tablist" aria-label="Admin sections">
          <button
            type="button" role="tab" aria-selected={tab === 'devices'}
            className={`btn ${tab === 'devices' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('devices')}
          >Devices</button>
          <button
            type="button" role="tab" aria-selected={tab === 'orders'}
            className={`btn ${tab === 'orders' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('orders')}
          >Orders</button>
          <button
            type="button" role="tab" aria-selected={tab === 'waitlist'}
            className={`btn ${tab === 'waitlist' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('waitlist')}
          >Waitlist</button>
        </div>

        {tab === 'orders' && <OrdersPanel />}
        {tab === 'waitlist' && <WaitlistPanel />}

        {tab === 'devices' && (<>
        {/* Summary cards */}
        <div className="admin-summary">
          <div className="card admin-summary__card">
            <div className="admin-summary__num">{totalAll}</div>
            <div className="admin-summary__label">Devices</div>
          </div>
          <div className="card admin-summary__card">
            <div className="admin-summary__num admin-summary__num--reef">{totalOnline}</div>
            <div className="admin-summary__label">Online</div>
          </div>
          <div className="card admin-summary__card">
            <div className={`admin-summary__num ${totalOffline > 0 ? 'admin-summary__num--copper' : ''}`}>{totalOffline}</div>
            <div className="admin-summary__label">Offline</div>
          </div>
          <div className="card admin-summary__card">
            <div className="admin-summary__num">{totalUsers}</div>
            <div className="admin-summary__label">Users</div>
          </div>
          <button type="button" className="card admin-summary__card admin-summary__card--btn"
            aria-expanded={alertsOpen} aria-controls="admin-fleet-alerts"
            onClick={() => setAlertsOpen((v) => !v)}>
            <div className="admin-summary__num">{alerts24h}</div>
            <div className="admin-summary__label">
              Alerts · 24 h <span className={`admin-sends__chev ${alertsOpen ? 'admin-sends__chev--open' : ''}`} aria-hidden="true">▸</span>
            </div>
          </button>
        </div>

        {/* Last 5 alerts sent anywhere in the fleet, with who and where. */}
        {alertsOpen && (
          <div id="admin-fleet-alerts" className="card admin-fleet-alerts">
            <div className="admin-summary__label admin-fleet-alerts__title">Last 5 alerts sent</div>
            {fleetAlerts.length === 0
              ? <div className="admin-sends__empty">No alerts sent since the send log started.</div>
              : <SendsTable rows={fleetAlerts} showWho />}
          </div>
        )}

        {/* Search + filters + sort */}
        <div className="admin-controls">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ID, name, or email…"
            className="admin-search"
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="admin-sort">
            <option value="all">All devices</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="leak">Leak protection</option>
            <option value="fault">Sensor fault</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="admin-sort">
            <option value="lastSeen">Sort: Last seen</option>
            <option value="name">Sort: Name</option>
            <option value="id">Sort: Device ID</option>
            <option value="status">Sort: Status</option>
            <option value="level">Sort: Tank 1 level</option>
          </select>
        </div>

        {loading && <div className="loading-page"><div className="spinner" /></div>}

        {error && !loading && (
          <div className="alert-banner warn" style={{ marginBottom: 12 }}>
            <div className="alert-banner-desc">{error}</div>
          </div>
        )}

        {/* Device list */}
        {!loading && (
          <div className="admin-list">
            {list.length === 0 && <div className="admin-empty">No devices match.</div>}

            {list.map(dev => (
              <button
                type="button"
                key={dev.deviceId}
                className={`card admin-row admin-row--btn ${dev.online ? 'admin-row--online' : 'admin-row--offline'}`}
                onClick={() => setSelected(dev)}
              >
                <div className="admin-row__header">
                  <StatusDot online={dev.online} />
                  <div className="admin-row__name">{dev.name || 'Unnamed'}</div>
                  <div className="admin-row__id">{dev.deviceId}</div>
                </div>

                <div className="admin-row__meta">
                  <span className="admin-row__email">{dev.ownerEmail || (dev.claimed ? '—' : 'Unclaimed')}</span>
                  {(plusChip(dev.plus) || (dev.leakDetection ? 'Plus' : null)) && (
                    <span className={`admin-chip ${dev.plus?.paymentFailed ? 'admin-chip--copper' : 'admin-chip--reef'}`}>
                      {plusChip(dev.plus) || 'Plus'}
                    </span>
                  )}
                  {(dev.reading?.f1 || dev.reading?.f2) && <span className="admin-chip admin-chip--copper">Fault</span>}
                </div>

                {dev.reading && (dev.reading.p1 != null || dev.reading.s2) && (
                  <div className="admin-row__tanks">
                    <div className="admin-tank">
                      <div className="admin-tank__label">{dev.tanks[0]?.name || 'Tank 1'}</div>
                      <div className="admin-tank__value">{Math.round(dev.reading.p1 ?? 0)}<span className="admin-tank__unit">%</span></div>
                      <div className="admin-tank__sub">
                        {fmtGal(dev.tanks[0]?.gallons)} · {fmtSensorIn(dev.tanks[0]?.sensorFt)}
                      </div>
                    </div>
                    {dev.reading.s2 && (
                      <div className="admin-tank">
                        <div className="admin-tank__label">{dev.tanks[1]?.name || 'Tank 2'}</div>
                        <div className="admin-tank__value">{Math.round(dev.reading.p2 ?? 0)}<span className="admin-tank__unit">%</span></div>
                        <div className="admin-tank__sub">
                          {fmtGal(dev.tanks[1]?.gallons)} · {fmtSensorIn(dev.tanks[1]?.sensorFt)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="admin-row__footer">
                  <div className="admin-row__owner">{dev.claims.length} {dev.claims.length === 1 ? 'account' : 'accounts'}</div>
                  <div className={`admin-row__status ${dev.online ? 'is-online' : ''}`}>
                    {dev.online ? 'Online' : 'Last seen ' + timeSince(dev.lastSeenMs)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && (
          <button type="button" className="btn btn-secondary" onClick={loadAll} style={{ maxWidth: '560px' }}>
            Refresh
          </button>
        )}
        </>)}

        <p className="admin-footnote">
          Admin console — {user?.email || 'signed in'}. Data served by the admin-only cloud function; claim codes are not shown here.
        </p>
      </div>

      {selected && <DeviceDetail dev={selected} onClose={() => setSelected(null)} onSaved={handleSaved} />}

      <BottomNav />
    </div>
  );
}
